'use client'

import { useReducer } from 'react'

import type {
  LinkItemWithStamp,
  TopNavV5MenuItem,
  TopNavV5SubMenu,
} from '@src/components/TopNavV5/TopNavV5Props'

type State = 'open' | 'closed' | 'left-menu-open' | 'left-menu-sub-menu-open' | 'sub-menu-open'

export type TopNavV5MobileState = {
  state: State
  previousState: State | null
  menuItems: TopNavV5MenuItem[]
  leftMenu: {
    title: string
    stamp?: string
    items: TopNavV5SubMenu[]
  } | null
  leftMenuIndex: number | null
  subMenu: TopNavV5SubMenu | null
  miniMenu: {
    title: string
    items: LinkItemWithStamp[]
  } | null
}

export type TopNavV5MobileAction =
  | {
      type: 'OPEN'
    }
  | {
      type: 'CLOSE_FROM_MAIN'
    }
  | {
      type: 'CLOSE_FROM_SUB_MENU'
    }
  | {
      type: 'CLOSE_FROM_LEFT_MENU'
    }
  | {
      type: 'CLOSE_FROM_LEFT_MENU_SUB_MENU'
    }
  | {
      type: 'OPEN_SUB_MENU'
      payload: {
        subMenu: TopNavV5SubMenu | null
        miniMenu: {
          title: string
          items: LinkItemWithStamp[]
        } | null
      }
    }
  | {
      type: 'OPEN_LEFT_MENU'
      payload: {
        leftMenu: {
          title: string
          stamp?: string
          items: TopNavV5SubMenu[]
        }
      }
    }
  | {
      type: 'OPEN_LEFT_MENU_SUB_MENU'
      payload: {
        leftMenuIndex: number
      }
    }
  | {
      type: 'BACK_FROM_LEFT_MENU'
    }
  | {
      type: 'BACK_FROM_SUB_MENU'
    }
  | {
      type: 'BACK_FROM_LEFT_MENU_SUB_MENU'
    }

const getInitialState = (menuItems: TopNavV5MenuItem[]): TopNavV5MobileState => {
  return {
    state: 'closed',
    previousState: null,
    menuItems,
    leftMenu: null,
    leftMenuIndex: null,
    subMenu: null,
    miniMenu: null,
  }
}

const reducer = (state: TopNavV5MobileState, action: TopNavV5MobileAction): TopNavV5MobileState => {
  const newState = {
    ...state,
    previousState: state.state,
  }
  switch (action.type) {
    case 'OPEN':
    case 'BACK_FROM_SUB_MENU':
    case 'BACK_FROM_LEFT_MENU':
      return { ...newState, state: 'open' }
    case 'OPEN_SUB_MENU':
      return {
        ...newState,
        state: 'sub-menu-open',
        subMenu: action.payload.subMenu,
        miniMenu: action.payload.miniMenu,
      }
    case 'CLOSE_FROM_MAIN':
    case 'CLOSE_FROM_SUB_MENU':
    case 'CLOSE_FROM_LEFT_MENU':
    case 'CLOSE_FROM_LEFT_MENU_SUB_MENU':
      return { ...newState, state: 'closed' }
    case 'OPEN_LEFT_MENU':
      return { ...newState, state: 'left-menu-open', leftMenu: action.payload.leftMenu }
    case 'OPEN_LEFT_MENU_SUB_MENU':
      return {
        ...newState,
        state: 'left-menu-sub-menu-open',
        leftMenuIndex: action.payload.leftMenuIndex,
      }
    case 'BACK_FROM_LEFT_MENU_SUB_MENU':
      return { ...newState, state: 'left-menu-open' }
  }
}

export const useTopNavV5MobileReducer = (menuItems: TopNavV5MenuItem[] = []) => {
  return useReducer(reducer, getInitialState(menuItems))
}
